import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, MessageCircle } from 'lucide-react';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    question: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Add your form submission logic here
    console.log('Form submitted:', formData);
    alert('Thank you for your question! We will get back to you soon.');
    setFormData({ name: '', email: '', question: '' });
  };

  const contactInfo = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: 'Phone',
      detail: '9730736858',
      link: 'tel:9730736858',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: 'Email',
      detail: 'Vegatasbike00@gmail.com',
      link: 'mailto:Vegatasbike00@gmail.com',
      color: 'bg-green-100 text-green-600'
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'Location',
      detail: 'Ashok Nagar, Tathawade, Pune',
      link: null,
      color: 'bg-red-100 text-red-600'
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'Working Hours',
      detail: 'Mon - Sun: 9:00 AM - 8:00 PM',
      link: null,
      color: 'bg-purple-100 text-purple-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Contact Us
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Ready to embark on your next journey with Vegatas Bike Rental Service? We're here to help!
          </p>
        </div>
      </div>

      {/* Introduction Section */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center max-w-4xl mx-auto">
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            Visit our head office in Pune, where our team will be delighted to assist you in selecting the perfect two-wheeler for your needs. Alternatively, you can contact us through our website or call our customer support hotline for reservations and inquiries.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            Join us in experiencing the joy of riding through Pune's streets, exploring its picturesque surroundings, and making memories that will last a lifetime. Vegatas Bike Rental Service is your trusted partner for two-wheeler adventures in the heart of Pune and beyond.
          </p>
        </div>
      </div>

      {/* Contact Info Cards */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
          Get In Touch
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {contactInfo.map((info, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6 text-center border border-gray-100 hover:-translate-y-1"
            >
              <div className={`${info.color} w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4`}>
                {info.icon}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {info.title}
              </h3>
              {info.link ? (
                <a
                  href={info.link}
                  className="text-gray-600 hover:text-blue-600 transition-colors duration-200 text-sm break-words"
                >
                  {info.detail}
                </a>
              ) : (
                <p className="text-gray-600 text-sm">{info.detail}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Main Content - Form and Address */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <div className="flex items-center mb-6">
              <MessageCircle className="w-8 h-8 text-blue-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">
                Have Any Questions?
              </h2>
            </div>
            <p className="text-gray-600 mb-8">
              Fill out the form below and we'll get back to you as soon as possible.
            </p>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 outline-none"
                  placeholder="Enter your name"
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Your Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 outline-none"
                  placeholder="Enter your email"
                />
              </div>
              <div>
                <label
                  htmlFor="question"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Your Question
                </label>
                <textarea
                  id="question"
                  name="question"
                  value={formData.question}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 outline-none resize-none"
                  placeholder="Enter your question or message"
                />
              </div>
              <button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 shadow-md hover:shadow-lg"
              >
                <Send className="w-5 h-5" />
                <span>Send Message</span>
              </button>
            </form>
          </div>

          {/* Address and Map Section */}
          <div className="space-y-6">
            {/* Address Card */}
            <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold mb-6">Visit Our Office</h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <MapPin className="w-6 h-6 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Address</h3>
                    <p className="text-blue-100 leading-relaxed">
                      AK Enterprises<br />
                      Ashok Nagar Road, Ashok Nagar,<br />
                      Tathawade, Pimpri-Chinchwad,<br />
                      Pune, Maharashtra 411033
                    </p>
                  </div>
                </div>
                <div className="border-t border-blue-500 pt-4 mt-4">
                  <a
                    href="tel:9730736858"
                    className="inline-flex items-center space-x-2 bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-200"
                  >
                    <Phone className="w-5 h-5" />
                    <span>Call: 9730736858</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Website Card */}
            <div className="bg-white rounded-2xl shadow-md p-8 border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Online Resources
              </h3>
              <div className="space-y-3">
                <a
                  href="https://www.Vegatasbike.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-3 text-blue-600 hover:text-blue-700 transition-colors duration-200"
                >
                  <span className="text-2xl">🌐</span>
                  <span className="font-medium">www.Vegatasbike.in</span>
                </a>
                <a
                  href="mailto:Vegatasbike00@gmail.com"
                  className="flex items-center space-x-3 text-blue-600 hover:text-blue-700 transition-colors duration-200"
                >
                  <span className="text-2xl">📧</span>
                  <span className="font-medium">Vegatasbike00@gmail.com</span>
                </a>
                <a
                  href="tel:9730736858"
                  className="flex items-center space-x-3 text-blue-600 hover:text-blue-700 transition-colors duration-200"
                >
                  <span className="text-2xl">📞</span>
                  <span className="font-medium">9730736858</span>
                </a>
              </div>
            </div>

            {/* Map - Updated for Tathawade Location */}
            <div className="bg-gray-100 rounded-2xl overflow-hidden shadow-md h-64">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3780.2837799134443!2d73.74299637517535!3d18.627598282563945!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2b9c7c1234567%3A0xabcdef1234567890!2sAshok%20Nagar%2C%20Tathawade%2C%20Pimpri-Chinchwad%2C%20Maharashtra%20411033!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Vegatas Bike Rental Location - Tathawade"
              />
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 py-16 px-4 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to Start Your Adventure?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Book your bike today and explore Pune with Vegatas!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:9730736858"
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-lg transition-colors duration-300 shadow-md hover:shadow-lg inline-flex items-center justify-center space-x-2"
            >
              <Phone className="w-5 h-5" />
              <span>Call Now</span>
            </a>
            <a
              href="https://www.Vegatasbike.in"
              className="bg-white hover:bg-gray-50 text-blue-600 font-semibold px-8 py-4 rounded-lg border-2 border-blue-600 transition-colors duration-300 inline-flex items-center justify-center"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
